import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import FormSelect from '../../../components/dropdown/formSelect';
import Button from '../../../components/button';
import {
  PermissionUsagePermissions,
  PermissionUsageType,
  SORTED_FIELD_PERMISSION,
  SORTED_MODULE_PERMISSION,
} from '../../../constants/common';
import { useGetFeaturesPermissionsDistributable, usePostPermissionUsage } from '../../../hooks';
import { useWealthlaneCustomerApi } from '../../../hooks/useWealthlaneCustomerApi';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { getFilteredPermissionData } from '../../../utils';
import { convertDualPermissions } from '../../../utils/permission/permissionAdapter';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import type { PermissionUsageResponseDto } from '@wealthlane/api-services/wealthlane-customer-service';

interface PermissionOption {
  label: string;
  value: string; // Full permission key like "CustomerService.Vendor.Commission" or "CustomerService.Customer.Participant"
}

const SearchPermissionOnUse = () => {
  const [formData, setFormData] = useState({
    type: [] as string[],
    permissionName: [] as string[],
    permissions: [] as string[],
  });
  const [usageData, setUsageData] = useState<PermissionUsageResponseDto | null>(null);

  const { handleRequest } = useWealthlaneCustomerApi();
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;

  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(handleRequest);
  const { mutate: searchPermissionUsage, isLoading } = usePostPermissionUsage(handleRequest);

  const [mergedData, setMergedData] = useState<any>();

  useEffect(() => {
    if (distributablePermissions?.fieldLevelPermissions) {
      setMergedData(
        distributablePermissions?.permissions?.concat(
          distributablePermissions?.fieldLevelPermissions
        )
      );
    }
  }, [distributablePermissions]);

  // Get filtered permission data - this handles both Customer and Vendor prefixes
  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertDualPermissions(mergedData ?? []),
    undefined, // Don't pass a specific prefix - let it handle all types
    allFieldValues
  );

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  // Create permission name options from both module and field level permissions
  // Store the FULL permission key (including prefix) as the value
  const permissionNameOptions = useMemo(() => {
    const options: PermissionOption[] = [];
    const addedKeys = new Set<string>();

    // Add module level permissions
    Object.keys(filterNonFieldLevelPermission).forEach((groupKey) => {
      const allInfo = filterNonFieldLevelPermission[groupKey]?.filter(
        (item: any) => !(item?.isDisabled as boolean) && item
      );

      if (allInfo?.length > 0) {
        const firstItem = allInfo[0];
        const fullKey = firstItem?.key; // Full key like "CustomerService.Vendor.Commission"
        const displayName = allFieldValues?.[fullKey];

        if (fullKey && displayName && !addedKeys.has(fullKey)) {
          addedKeys.add(fullKey);
          options.push({
            label: displayName,
            value: fullKey, // Store full key as value
          });
        }
      }
    });

    // Add field level permissions
    Object.keys(filterFieldLevelPermission).forEach((groupKey) => {
      const allInfo = filterFieldLevelPermission[groupKey]?.filter(
        (item: any) => !(item?.isDisabled as boolean) && item
      );

      if (allInfo?.length > 0) {
        const firstItem = allInfo[0];
        const fullKey = firstItem?.key;
        const displayName = allFieldValues?.[fullKey];

        if (fullKey && displayName && !addedKeys.has(fullKey)) {
          addedKeys.add(fullKey);
          options.push({
            label: displayName,
            value: fullKey,
          });
        }
      }
    });

    // Sort options alphabetically by label
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [filterNonFieldLevelPermission, filterFieldLevelPermission, allFieldValues]);

  // Build request body from selected permissionName and permissions
  // permissionName now contains full keys like "CustomerService.Vendor.Commission"
  const buildPermissionStrings = (): string[] => {
    const permissionStrings: string[] = [];

    formData.permissionName.forEach((fullPermKey: string) => {
      // fullPermKey is like "CustomerService.Vendor.Commission" or "CustomerService.Customer.Participant"
      if (formData.permissions.length === 0 || formData.permissions.includes('all')) {
        // If no specific permission selected or "All" is selected, add base permission and all variants
        permissionStrings.push(fullPermKey); // View permission (base)
        permissionStrings.push(`${fullPermKey}.Create`);
        permissionStrings.push(`${fullPermKey}.Edit`);
        permissionStrings.push(`${fullPermKey}.Delete`);
      } else {
        // Add specific permissions based on selection
        formData.permissions.forEach((perm: string) => {
          if (perm === 'view') {
            permissionStrings.push(fullPermKey);
          } else {
            const capitalizedPerm = perm.charAt(0).toUpperCase() + perm.slice(1);
            permissionStrings.push(`${fullPermKey}.${capitalizedPerm}`);
          }
        });
      }
    });

    return permissionStrings;
  };

  const handleSearch = () => {
    const types = formData.type.map((t: any) => t.label || t);
    const permissions = buildPermissionStrings();

    searchPermissionUsage(
      {
        types,
        permissions,
      },
      {
        onSuccess: (data) => {
          if (data) {
            setUsageData(data);
          }
        },
      }
    );
  };

  return (
    <>
      <PageHeader data-page='searchPermissionOnUse' id='pageContainer'>
        <Heading variant='h3' title='Permission On Use' id='pageTitle' />
        <Heading variant='body-s' title={`Search for permission on use.`} id='pageDescription' />
      </PageHeader>
      <div className='grid grid-cols-4 items-center gap-4'>
        <FormSelect
          label='Type'
          name='type'
          id='type'
          onChange={(e: any) => {
            setFormData({ ...formData, type: e });
          }}
          values={formData.type}
          arrValue={formData.type}
          options={PermissionUsageType}
          isFormik={false}
          multiple={true}
          required
        />
        <FormSelect
          label='Permission Name'
          name='permissionName'
          id='permissionName'
          onChange={(e: any) => {
            setFormData({ ...formData, permissionName: e });
          }}
          values={formData.permissionName}
          arrValue={formData.permissionName}
          options={permissionNameOptions}
          isFormik={false}
          multiple={true}
          required
        />
        <FormSelect
          label='Permissions'
          name='permissions'
          id='permissions'
          onChange={(e: any) => {
            setFormData({ ...formData, permissions: e });
          }}
          values={formData.permissions}
          arrValue={formData.permissions}
          options={PermissionUsagePermissions}
          isFormik={false}
          multiple={true}
          required
        />
        <div className='flex justify-center mt-2'>
          <Button variant='primary' title='Search' onClick={handleSearch} loading={isLoading} />
        </div>
      </div>

      {/* Results Section */}
      {usageData && !isLoading && (
        <div style={{ marginTop: '20px', display: 'flex', gap: '40px' }}>
          {/* Feature Sets Section */}
          {usageData.featureSets && usageData.featureSets.length > 0 && (
            <div style={{ flex: 1 }} id='featureSetsContainer'>
              <Heading variant='h4' title='Used Feature Sets' />
              {usageData.featureSets && usageData.featureSets.length > 0 ? (
                <ul
                  style={{ listStyle: 'none', padding: 0, marginTop: '10px' }}
                  id='featureSetsList'>
                  {usageData.featureSets.map((featureSet) => (
                    <li
                      key={featureSet.id}
                      id={`featureSetContainer`}
                      style={{
                        padding: '8px 12px',
                        marginBottom: '8px',
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                      }}>
                      <Heading variant='body-s' title={featureSet.name} id={`featureSetName`} />
                    </li>
                  ))}
                </ul>
              ) : (
                <Heading variant='body-s' title='No feature sets found.' />
              )}
            </div>
          )}
          {/* Roles Section */}
          {usageData.roles && usageData.roles.length > 0 && (
            <div style={{ flex: 1 }} id='rolesContainer'>
              <Heading variant='h4' title='Used Roles' />
              {usageData.roles && usageData.roles.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, marginTop: '10px' }} id='rolesList'>
                  {usageData.roles.map((role) => (
                    <li
                      key={role.id}
                      id={`roleContainer`}
                      style={{
                        padding: '8px 12px',
                        marginBottom: '8px',
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                      }}>
                      <Heading variant='body-s' title={role.name} id={`roleName`} />
                    </li>
                  ))}
                </ul>
              ) : (
                <Heading variant='body-s' title='No feature sets found.' className='mt-5' />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SearchPermissionOnUse;
